import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Grid,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  BottomNavigation,
  BottomNavigationAction,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { doc, getDoc } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import PieChart from './PieChart';
import HeatMapCalendar from './HeatMapCalendar';
import HabitAnalyzer from './HabitAnalyzer';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { auth, db } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import {
  Dashboard as DashboardIcon,
  BarChart as BarChartIcon,
  ListAlt as ListAltIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import AddExpense from '../AddExpense';
import { AddCircle as AddCircleIcon } from '@mui/icons-material';


const Statistics = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [userFamily, setUserFamily] = useState(null);
  const [error, setError] = useState('');
  const [selectedYear, setSelectedYear] = useState(format(new Date(), 'yyyy'));
  const [selectedMonth, setSelectedMonth] = useState(format(new Date(), 'MM'));
  const [availableYears, setAvailableYears] = useState([]);
  const availableMonths = ['01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11', '12'];
  const [bottomNavValue, setBottomNavValue] = useState('statistics');
  const bottomNavHeight = theme.mixins.toolbar.minHeight + 16;
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);

  useEffect(() => {
    const fetchUserFamily = async (user) => {
      if (!user) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.familyId) {
            setUserFamily({ id: userData.familyId });
          } else {
            setUserFamily(null);
          }
        }
        const currentYear = new Date().getFullYear();
        const years = Array.from({ length: 10 }, (_, i) => (currentYear - i).toString()).reverse();
        setAvailableYears(years);
      } catch (err) {
        console.error('Errore nel recupero dei dati utente:', err);
        setError("Errore nel recupero dei dati dell'utente.");
      } finally {
        setLoading(false);
      }
    };

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        fetchUserFamily(user);
      } else {
        setLoading(false);
        setError('Utente non autenticato.');
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const handleYearChange = (event) => {
    setSelectedYear(event.target.value);
  };

  const handleMonthChange = (event) => {
    setSelectedMonth(event.target.value);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: 2 }}>
        <Typography variant="body1" color="error">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'background.default',
        minHeight: '100vh',
        pb: `${bottomNavHeight}px`,
      }}
    >
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, md: 4 },
          maxWidth: '1200px',
          margin: '0 auto',
          width: '100%',
          boxSizing: 'border-box',
          mb: `${bottomNavHeight}px`,
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: 'text.primary' }}>
            Statistiche
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/dashboard')}
            sx={{ borderRadius: '12px' }}
          >
            Torna alla Dashboard
          </Button>
        </Box>

        <Grid container spacing={4}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel id="year-select-label">Anno</InputLabel>
                <Select
                  labelId="year-select-label"
                  value={selectedYear}
                  label="Anno"
                  onChange={handleYearChange}
                >
                  {availableYears.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel id="month-select-label">Mese</InputLabel>
                <Select
                  labelId="month-select-label"
                  value={selectedMonth}
                  label="Mese"
                  onChange={handleMonthChange}
                >
                  {availableMonths.map((month) => (
                    <MenuItem key={month} value={month}>
                      {format(new Date(`2023-${month}-01`), 'MMMM', { locale: it })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Statistiche Mensili
            </Typography>
            <PieChart type="monthly" year={selectedYear} month={selectedMonth} userFamily={userFamily} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Statistiche Annuali
            </Typography>
            <PieChart type="annual" year={selectedYear} userFamily={userFamily} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Heatmap del Mese
            </Typography>
            <HeatMapCalendar year={selectedYear} month={selectedMonth} userFamily={userFamily} />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h6" gutterBottom>
              Analizzatore di Abitudini
            </Typography>
            <HabitAnalyzer year={selectedYear} month={selectedMonth} userFamily={userFamily} />
          </Grid>
        </Grid>
      </Box>

      <BottomNavigation
        value={bottomNavValue}
        onChange={(event, newValue) => setBottomNavValue(newValue)}
        showLabels
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: theme.palette.mode === 'dark'
            ? 'rgba(18, 18, 18, 0.8)'
            : 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(0,0,0,0.1)',
          zIndex: theme.zIndex.appBar,
          '& .MuiBottomNavigationAction-root': {
            color: theme.palette.text.secondary,
            '&.Mui-selected': { color: theme.palette.primary.main },
          },
        }}
      >
        <BottomNavigationAction
          label="Dashboard"
          value="dashboard"
          icon={<DashboardIcon />}
          onClick={() => navigate('/dashboard')}
        />
        <BottomNavigationAction
          label="Statistiche"
          value="statistics"
          icon={<BarChartIcon />}
          onClick={() => navigate('/statistics')}
        />
        <BottomNavigationAction
          label="Spese"
          value="expense-list"
          icon={<ListAltIcon />}
          onClick={() => navigate('/expense-list', { state: { userFamily } })}
        />
        <BottomNavigationAction
          label="Aggiungi"
          value="add-expense"
          icon={<AddCircleIcon />}
          onClick={() => setAddExpenseOpen(true)}
        />
      </BottomNavigation>

      <Dialog
        open={addExpenseOpen}
        onClose={() => setAddExpenseOpen(false)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 0,
            border: '1px solid',
            borderColor: theme.palette.mode === 'light' ? '#000' : '#fff',
            backgroundColor: 'background.paper',
          },
        }}
      >
        <DialogTitle sx={{ color: 'text.primary' }}>Aggiungi Spesa</DialogTitle>
        <DialogContent>
          <AddExpense onClose={() => setAddExpenseOpen(false)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddExpenseOpen(false)} sx={{ cursor: 'pointer' }}>
            Chiudi
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Statistics;
